import { getCurrentCredentials, getCurrentUser, updatePassword } from '../../components/utils/amplifyAuthHelpers';
import axios from 'axios';
import { toggleAlertMessage } from '../action';
import createSignedRequest from './createSignedRequest';
import { getOrganisationUsers } from './organisationActions';
import { versionToPostfix } from '../../components/utils/GlobalFunctions';

let { apiURL, paymentURL, getAppVersion } = require('./apiURL');

const Types = {
  AUTHENTICATION_LOGIN: 'AUTHENTICATION_LOGIN',
  AUTHENTICATION_PROCESSING: 'AUTHENTICATION_PROCESSING',
  AUTHENTICATION_LOGOUT: 'AUTHENTICATION_LOGOUT',
  AUTHENTICATION_VALIDATE_CODE: 'AUTHENTICATION_VALIDATE_CODE',
  AUTHENTICATION_RECOVERY_CODE_CHECK: 'AUTHENTICATION_RECOVERY_CODE_CHECK',
  AUTHENTICATION_SETTING_PASSWORD: 'AUTHENTICATION_SETTING_PASSWORD',
  AUTHENTICATION_SET_PASSWORD_RESULT: 'AUTHENTICATION_SET_PASSWORD_RESULT',
  AUTHENTICATION_LOGIN_COGNITO: 'AUTHENTICATION_LOGIN_COGNITO',
  AUTHENTICATION_USER_CREDENTIALS: 'AUTHENTICATION_USER_CREDENTIALS',
  AUTHENTICATION_NEW_USER_DETAILS: 'AUTHENTICATION_NEW_USER_DETAILS',
  AUTHENTICATION_REGISTRATION_DETAILS: 'AUTHENTICATION_REGISTRATION_DETAILS',
  AUTHENTICATION_ACTIVE_CAMPAIGN_DETAILS: 'AUTHENTICATION_ACTIVE_CAMPAIGN_DETAILS',
  AUTHENTICATION_CLOSE_USER_AGREEMENT_MODAL: 'AUTHENTICATION_CLOSE_USER_AGREEMENT_MODAL',
  UPDATE_LEGAL_AGREEMENT: 'AUTHENTICATION_UPDATE_LEGAL_AGREEMENT',
  AUTHENTICATION_INVITE_NEW_USER: 'AUTHENTICATION_INVITE_NEW_USER',
  LIST_WHITE_LABEL_SETTINGS: 'AUTHENTICATION_LIST_WHITE_LABEL_SETTINGS',
};

export const loggingIn = (saving) => ({
  type: Types.AUTHENTICATION_PROCESSING,
  saving,
});

const loginWithCognito = (username, credentials) => ({
  type: Types.AUTHENTICATION_LOGIN_COGNITO,
  payload: username,
  credentials,
});

const updateLoginStatus = (response, attempt) => ({
  type: Types.AUTHENTICATION_LOGIN,
  payload: response,
  attempt,
});

const loginWithAws = (username) => {
  return (dispatch) => {
    dispatch(loggingIn(true));
    dispatch(loginWithCognito(username));
  };
};

const logoutWithAws = (response) => ({
  type: Types.AUTHENTICATION_LOGOUT,
  payload: response,
});

const logout = (response) => {
  return (dispatch) => {
    dispatch(logoutWithAws(response));
  };
};

const isLoggedIn = () => {
  return (dispatch, getState) => {
    dispatch(loggingIn(true));

    fetch(apiURL + '/authentication/status', {
      credentials: 'include',
      method: 'GET',
      // body: JSON.stringify({username,password}),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        //'Organisation': getState().currentOrganisation,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('entity data', data);
        dispatch(updateLoginStatus(data, false));
      });
  };
};

const getUserCredentials = () => {
  return async (dispatch) => {
    const userCredentials = await getCurrentCredentials();

    dispatch(currentUserCredentials(userCredentials));
  };
};

const currentUserCredentials = (userCredentials) => ({
  type: Types.AUTHENTICATION_USER_CREDENTIALS,
  payload: userCredentials,
});

/**
 * Store registration form payload (and optional trial meta) before confirm/checkout.
 * @param {object|false} user
 * @param {object} organisation
 * @param {{ trial?: boolean, leadId?: string|null }} [meta]
 */
const newUserSubscription = (user, organisation, meta = {}) => ({
  type: Types.AUTHENTICATION_REGISTRATION_DETAILS,
  payload: { user, organisation, trial: meta.trial === true, leadId: meta.leadId || null },
});

const newUserDetails = (response) => ({
  type: Types.AUTHENTICATION_NEW_USER_DETAILS,
  payload: response,
});

/**
 * Create user + org via POST /payment/stripe/userRegistration.
 * For free trials pass `{ trial: true, leadId }` — skips Stripe Subscription (Customer only).
 * Caller should refetch GET /my/organisation afterwards (savedOrg is raw Dynamo).
 *
 * @param {object} user
 * @param {object} organisation
 * @param {{ trial?: boolean, leadId?: string|null }} [options]
 * @returns {Function} thunk resolving to response data
 */
const newUser = (user, organisation, options = {}) => {
  return async (dispatch) => {
    const { trial, leadId } = options;
    const body = { user, organisation };
    if (trial === true) {
      body.trial = true;
      if (leadId) body.leadId = leadId;
    }

    try {
      const response = await axios(
        await createSignedRequest('POST', paymentURL + '/payment/stripe/userRegistration', JSON.stringify(body))
      );

      const data = response.data || {};
      if (data.error) {
        const err = new Error(data.message || data.error);
        err.code = data.error;
        err.response = { data, status: response.status };
        throw err;
      }

      dispatch(newUserDetails(data));
      if (data.organisationId) {
        localStorage.setItem('Organisation', data.organisationId);
      }
      return data;
    } catch (e) {
      console.log('newUser Error:', e);
      const apiError = e?.response?.data;
      if (apiError?.error && !e.code) {
        e.code = apiError.error;
        e.message = apiError.message || e.message;
      }
      throw e;
    }
  };
};

const createContact = (user) => {
  return async () => {
    fetch(apiURL + `/registration/createContact`, {
      method: 'POST',
      body: JSON.stringify({ user }),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    })
      .then((response) => response.json())
      .then((data) => console.log('createContact', data));
  };
};

const inviteNewUser = (user) => {
  return async (dispatch, getState) => {
    const state = getState();
    const { whiteLabelId } = state.whiteLabelAdmin;

    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/my/organisation/inviteNewUser',
          JSON.stringify({ user, whiteLabelId }),
          {
            Organisation: String(state.currentOrganisation),
          }
        )
      );

      const { message, success } = response.data || {};

      dispatch(toggleAlertMessage(true, message, success ? 'success' : 'error'));
      dispatch(getOrganisationUsers());
    } catch (e) {
      console.log('inviteNewUser Error:', e);
      dispatch(toggleAlertMessage(true, e.message, 'error'));
    }
  };
};

const updateLegalAgreements = (isOrg) => ({
  type: Types.UPDATE_LEGAL_AGREEMENT,
  payload: isOrg,
});

const confirmUserAgreement = (incOrg) => {
  return async (dispatch, getState) => {
    const state = getState();
    const isOrg = incOrg;

    try {
      const response = await axios(
        await createSignedRequest('POST', apiURL + '/my/organisation/confirmUserAgreement', JSON.stringify({ isOrg }), {
          Organisation: String(state.currentOrganisation),
        })
      );

      dispatch(updateLegalAgreements(isOrg));

      console.log('confirmUserAgreement Response', response.data);
    } catch (e) {
      console.log('confirmUserAgreement Error:', e);
    }
  };
};

const listWLSettings = (response) => ({
  type: Types.LIST_WHITE_LABEL_SETTINGS,
  response,
});

/**
 * Loads the white label settings JSON published on S3 for the current domain and
 * stores it in Redux (`whiteLabelAdmin` + `branding`).
 *
 * @param {Object} [options]
 * @param {boolean} [options.bypassCache=false] - Skip the browser/disk cache. Use after saving
 * theme settings, otherwise the freshly published file is served from cache.
 * @returns {Function} Redux thunk.
 * @example
 * await saveWLSettings(settings);
 * await getWLSettings({ bypassCache: true });
 */
const getWLSettings = ({ bypassCache = false } = {}) => {
  const version = getAppVersion();
  const postfix = version === 'v1' ? '' : `-${versionToPostfix(version)}`;
  // Local override (gitignored .env.local): REACT_APP_WL_DOMAIN=app.compareyourfootprint.com to load another host's branding.
  const wlDomain =
    process.env.REACT_APP_WL_DOMAIN ||
    (['localhost'].includes(window.location.hostname) ? 'dev.app.compareyourfootprint.com' : window.location.hostname);
  const archer = window.location.hostname.includes('archerirm');
  const link = archer
    ? `https://s3.eu-west-2.amazonaws.com/archerim.downloads.compareyourfootprint.com/whiteLabelSettings${postfix}/${wlDomain}.json`
    : `https://s3.eu-west-2.amazonaws.com/downloads.compareyourfootprint.com/whiteLabelSettings${postfix}/${wlDomain}.json`;

  return async (dispatch) => {
    // Cache buster via query string: a Cache-Control request header would force a CORS preflight on S3.
    const response = await axios(bypassCache ? `${link}?t=${Date.now()}` : link)
      .then((res) => res.data)
      .catch((err) => {
        console.log('getWLSettings Error', err);
        return {};
      });

    dispatch(listWLSettings(response));
  };
};

const sendEmailToSalesTeam = (org, user) => {
  return async () => {
    fetch(apiURL + `/registration/sendEmailToSalesTeam`, {
      method: 'POST',
      body: JSON.stringify({ org, user }),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('sendEmailToSalesTeam', data);
      })
      .catch((err) => console.log('sendEmailToSalesTeam', err));
  };
};

const setUserPassword = (oldPassword, newPassword) => {
  return async (dispatch) => {
    await getCurrentUser();
    await updatePassword({ oldPassword, newPassword });
  };
};

const Actions = {
  isLoggedIn,
  loginWithAws,
  getUserCredentials,
  logout,
  newUser,
  newUserSubscription,
  loggingIn,
  createContact,
  inviteNewUser,
  confirmUserAgreement,
  getWLSettings,
  sendEmailToSalesTeam,
  setUserPassword,
};

export { Actions, Types };
